import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SecurityContext,
  ViewChild,
} from "@angular/core";
import { MpSdk, ShowcaseBundleWindow } from "@matterport/webcomponent";
import { Observable, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { MattertagAssetLink } from "../matterport-page/mattertag-asset-link.model";
import { MattertagInjectorService } from "./mattertag-injector.service";
import { environment } from "../../../../environments/environment";
import { DomSanitizer, SafeValue } from "@angular/platform-browser";
import MattertagData = MpSdk.Tag.TagData;

@Component({
  selector: "ngx-matterport-viewer",
  templateUrl: "./matterport-viewer.component.html",
  styleUrls: ["./matterport-viewer.component.scss"],
  imports: [],
})
export class MatterportViewerComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  public matterportModelUrl: SafeValue;

  // eslint-disable-next-line no-restricted-syntax
  public get mattertagList$(): Observable<MattertagData[]> {
    if (!this._mattertagList$) {
      throw new Error("Matterport is not connected yet!");
    }
    return this._mattertagList$;
  }

  private _mattertagList$: Observable<MattertagData[]>;

  @Input() public matterportModelSid: string;

  @Output() public readonly mattertagSelected = new EventEmitter<string>();
  @Output() public readonly mattertagHovered = new EventEmitter<string>();
  @Output() public readonly mattertagDocked = new EventEmitter<string>();
  @Output() public readonly matterportConnected = new EventEmitter<MpSdk>();
  @Output() public readonly matterportSandboxRegistered =
    new EventEmitter<string>();

  @ViewChild("iframe") private iframe: ElementRef<HTMLIFrameElement>;

  private mpSdk: MpSdk;
  private subscriptions: MpSdk.ISubscription[] = [];

  public constructor(
    private readonly mattertagInjectorService: MattertagInjectorService,
    private readonly domSanitizer: DomSanitizer,
  ) {}

  public ngOnInit(): void {
    const showcaseParams = new URLSearchParams();
    showcaseParams.set("m", this.matterportModelSid);
    showcaseParams.set("applicationKey", environment.matterport.applicationKey);
    showcaseParams.set("apiHost", "https://my.matterport.com");
    showcaseParams.set("hr", "0");
    showcaseParams.set("qs", "1");
    showcaseParams.set("play", "1");
    showcaseParams.set("title", "0");
    this.matterportModelUrl = this.domSanitizer.sanitize(
      SecurityContext.RESOURCE_URL,
      `./assets/@matterport/bundle/showcase.html?${showcaseParams}`,
    );
  }

  public ngAfterViewInit(): void {
    const showcaseIframe = this.iframe.nativeElement;
    showcaseIframe.addEventListener("load", async () => {
      const contentWindow =
        showcaseIframe.contentWindow as ShowcaseBundleWindow;
      if (!contentWindow?.MP_SDK) {
        throw new Error(
          "Could not find a reference to the Matterport SDK on the iframe",
        );
      }

      const sdk = await contentWindow.MP_SDK.connect(contentWindow);
      this.onMatterportConnect(sdk);
    });
  }

  private readonly destroyed$ = new Subject<void>();

  public ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.cancel());
    this.destroyed$.next();
  }

  private onMatterportConnect(mpSdk: MpSdk): void {
    this.mpSdk = mpSdk;
    const subscription = mpSdk.Tag.openTags.subscribe((data) => {
      this.mattertagDocked.next(data.docked);
      this.mattertagHovered.next(data.hovered);
      data.selected.forEach((tagId) => this.mattertagSelected.next(tagId));
    });
    this.subscriptions.push(subscription);

    this._mattertagList$ = this.getMattertagDataList$(mpSdk);

    this.matterportConnected.next(mpSdk);

    this.mattertagInjectorService
      .registerSandbox(mpSdk)
      .then((sandboxId) => this.matterportSandboxRegistered.next(sandboxId));
  }

  public async linkMattertagToHelix(
    link: MattertagAssetLink,
    tagSid: string,
  ): Promise<void> {
    await this.mattertagInjectorService.linkMattertagToHelix(
      this.mpSdk,
      this.destroyed$,
      link,
      tagSid,
    );
  }

  private getMattertagDataList$(mpSdk: MpSdk): Observable<MpSdk.Tag.TagData[]> {
    const changed$ = new Subject<MpSdk.Dictionary<MpSdk.Tag.TagData>>();
    const subscription = mpSdk.Tag.data.subscribe({
      onAdded: (_index, _item, collection) => changed$.next(collection),
      onRemoved: (_index, _item, collection) => changed$.next(collection),
      onUpdated: (_index, _item, collection) => changed$.next(collection),
      onCollectionUpdated: (collection) => changed$.next(collection),
    });
    this.subscriptions.push(subscription);
    return changed$.pipe(
      map((collection) => [...collection].map(([_id, tagData]) => tagData)),
    );
  }
}
